/**
 * VibeCheck CLI v2 — Theme & Branding
 * Colors, icons, and branding constants for consistent CLI output.
 */

import chalk from 'chalk';

// ── Colors ──────────────────────────────────────────────────────────────────

export const colors = {
  brand: chalk.hex('#bc13fe'),
  brandDim: chalk.hex('#8b5cf6'),
  success: chalk.green,
  error: chalk.red,
  warning: chalk.yellow,
  info: chalk.cyan,
  dim: chalk.dim,
  bold: chalk.bold,
  white: chalk.white,
  gray: chalk.gray,
  underline: chalk.underline,
  // Severity
  critical: chalk.red.bold,
  high: chalk.hex('#f97316'),
  medium: chalk.yellow,
  low: chalk.blue,
};

// ── Icons ───────────────────────────────────────────────────────────────────

const isUnicode = process.platform !== 'win32' || !!process.env.WT_SESSION || !!process.env.TERM_PROGRAM;

export const icons = isUnicode ? {
  success: '✓',
  error: '✗',
  warning: '⚠',
  info: 'ℹ',
  bullet: '•',
  arrow: '→',
  arrowRight: '▸',
  star: '★',
  rocket: '🚀',
  fire: '🔥',
  sparkles: '✨',
  shield: '🛡️',
  check: '☑',
  lock: '🔒',
  lightning: '⚡',
  clock: '⏱',
  folder: '📁',
  file: '📄',
  gear: '⚙',
  chart: '📊',
  package: '📦',
  doctor: '🩺',
  globe: '🌐',
  hammer: '🔨',
  polish: '✨',
  book: '📖',
  target: '🎯',
  brain: '🧠',
  magnify: '🔍',
  ship: '🚀',
  block: '🚫',
  pass: '✅',
  fail: '❌',
  warn: '⚠️',
  dash: '─',
} : {
  success: '√',
  error: '×',
  warning: '!',
  info: 'i',
  bullet: '*',
  arrow: '->',
  arrowRight: '>',
  star: '*',
  rocket: '>>',
  fire: '(!)',
  sparkles: '*',
  shield: '[#]',
  check: '[x]',
  lock: '[L]',
  lightning: '!',
  clock: '[T]',
  folder: '[D]',
  file: '[F]',
  gear: '[G]',
  chart: '[C]',
  package: '[P]',
  doctor: '[+]',
  globe: '[W]',
  hammer: '[H]',
  polish: '*',
  book: '[B]',
  target: '[>]',
  brain: '[~]',
  magnify: '[?]',
  ship: '>>',
  block: '[X]',
  pass: '[OK]',
  fail: '[FAIL]',
  warn: '[WARN]',
  dash: '-',
};

// ── Branding ────────────────────────────────────────────────────────────────

export const CLI_NAME = 'vibecheck';
export const CLI_VERSION_KEY = 'version';
export const DOCS_URL = 'https://vibecheck.dev/docs';

export function brandName(): string {
  return colors.brand('VibeCheck');
}

export function brandTagline(): string {
  return colors.dim('Catch hallucinations, security issues, and quality problems before they ship.');
}

// ── Severity Formatting ─────────────────────────────────────────────────────

export function severityColor(severity: string): (text: string) => string {
  switch (severity) {
    case 'critical': return colors.critical;
    case 'high': return colors.high;
    case 'medium': return colors.medium;
    case 'low': return colors.low;
    case 'info': return colors.dim;
    default: return colors.dim;
  }
}

export function severityIcon(severity: string): string {
  switch (severity) {
    case 'critical': return colors.critical('🚨');
    case 'high': return colors.high('⚠️');
    case 'medium': return colors.medium('📋');
    case 'low': return colors.low('ℹ️');
    case 'info': return colors.dim('💡');
    default: return '';
  }
}

export function statusIcon(status: string): string {
  switch (status) {
    case 'passed': return colors.success(icons.success);
    case 'failed': return colors.error(icons.error);
    case 'warning': return colors.warning(icons.warning);
    case 'skipped': return colors.dim(icons.dash);
    default: return '';
  }
}
